import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapGeoAudit,
  mapGeoCandidate,
  mapGeoExecuteResult,
  mapGeoRunDetail,
  mapGeoRunPage,
  mapGeoRuntimeStatus,
  mapGeoTargetItem,
  mapGeoTargetPage,
  type GeoAuditRaw,
  type GeoCandidateRaw,
  type GeoExecutePayloadRaw,
  type GeoRunDetailRaw,
  type GeoRunPageRaw,
  type GeoRuntimeStatusRaw,
  type GeoTargetItemRaw,
  type GeoTargetPageRaw,
} from "../api/adapters";
import type {
  GeoAudit,
  GeoCandidate,
  GeoExecuteResult,
  GeoRunCreateInput,
  GeoRunDetail,
  GeoRunFilters,
  GeoRunPage,
  GeoRuntimeStatus,
  GeoTargetFilters,
  GeoTargetItem,
  GeoTargetPage,
} from "../types";

/** 查询 GEO 治理外部服务运行状态。 */
export async function fetchGeoRuntimeStatus(): Promise<GeoRuntimeStatus> {
  const response = await httpClient.get<ApiEnvelope<GeoRuntimeStatusRaw>>(
    "/geo-governance/runtime-status",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理运行状态接口返回了无效数据");
  }
  return mapGeoRuntimeStatus(response.data.data);
}

/** 查询 GEO 治理批次。 */
export async function fetchGeoRuns(filters: GeoRunFilters): Promise<GeoRunPage> {
  const response = await httpClient.get<ApiEnvelope<GeoRunPageRaw>>("/geo-governance/runs", {
    params: {
      page: filters.page,
      page_size: filters.pageSize,
      status: filters.status,
      keyword: filters.keyword || undefined,
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理批次接口返回了无效数据");
  }
  return mapGeoRunPage(response.data.data);
}

/** 查询 GEO 治理批次详情。 */
export async function fetchGeoRunDetail(runId: string): Promise<GeoRunDetail> {
  const response = await httpClient.get<ApiEnvelope<GeoRunDetailRaw>>(
    `/geo-governance/runs/${runId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理批次详情接口返回了无效数据");
  }
  return mapGeoRunDetail(response.data.data);
}

/** 创建历史 GEO 治理批次。 */
export async function createGeoRun(input: GeoRunCreateInput): Promise<GeoRunDetail> {
  const response = await httpClient.post<ApiEnvelope<GeoRunDetailRaw>>("/geo-governance/runs", {
    batch_name: input.batchName.trim(),
    target_types: input.targetTypes,
    dry_run: input.dryRun,
    target_limit_per_type: input.targetLimitPerType,
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理批次创建接口返回了无效数据");
  }
  return mapGeoRunDetail(response.data.data);
}

/** 分块执行历史 GEO 治理批次。 */
export async function executeGeoRun(runId: string, limit: number): Promise<GeoExecuteResult> {
  const response = await httpClient.post<ApiEnvelope<GeoExecutePayloadRaw>>(
    `/geo-governance/runs/${runId}/execute`,
    { limit },
    // 单条 LLM/高德调用可能较慢；20 条串行治理允许在服务端完成后再刷新页面。
    { timeout: 600_000 },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理批次执行接口返回了无效数据");
  }
  return mapGeoExecuteResult(response.data.data);
}

/** 查询批次内治理对象。 */
export async function fetchGeoTargets(
  runId: string,
  filters: GeoTargetFilters,
): Promise<GeoTargetPage> {
  const response = await httpClient.get<ApiEnvelope<GeoTargetPageRaw>>(
    `/geo-governance/runs/${runId}/targets`,
    {
      params: {
        page: filters.page,
        page_size: filters.pageSize,
        status: filters.status,
        target_type: filters.targetType,
        reason: filters.reason || undefined,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 治理对象接口返回了无效数据");
  }
  return mapGeoTargetPage(response.data.data);
}

/** 查询治理对象候选。 */
export async function fetchGeoCandidates(targetId: string): Promise<GeoCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<GeoCandidateRaw[]>>(
    `/geo-governance/targets/${targetId}/candidates`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 候选接口返回了无效数据");
  }
  return response.data.data.map(mapGeoCandidate);
}

/** 查询治理对象审计。 */
export async function fetchGeoAudits(targetId: string): Promise<GeoAudit[]> {
  const response = await httpClient.get<ApiEnvelope<GeoAuditRaw[]>>(
    `/geo-governance/targets/${targetId}/audits`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 审计接口返回了无效数据");
  }
  return response.data.data.map(mapGeoAudit);
}

/** 选择候选并写回历史 GEO。 */
export async function approveGeoTarget(
  targetId: string,
  candidateId: string,
  note: string,
): Promise<void> {
  await postGeoTargetAction(targetId, "approve", { candidate_id: candidateId, note });
}

/** 跳过无法确认的对象。 */
export async function skipGeoTarget(targetId: string, note: string): Promise<void> {
  await postGeoTargetAction(targetId, "skip", { note });
}

/** 将技术失败对象重新放回待处理。 */
export async function retryGeoTarget(targetId: string): Promise<void> {
  await postGeoTargetAction(targetId, "retry");
}

/** 重新读取业务主档；已补齐坐标时直接完成，否则刷新快照后重新排队。 */
export async function refreshGeoTargetBusinessData(targetId: string): Promise<GeoTargetItem> {
  const response = await httpClient.post<ApiEnvelope<GeoTargetItemRaw>>(
    `/geo-governance/targets/${targetId}/refresh-business-data`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("业务资料刷新接口返回了无效数据");
  }
  return mapGeoTargetItem(response.data.data);
}

/**
 * 提交治理对象的审核、跳过或重试动作。
 * @param targetId 治理对象 ID
 * @param action 目标动作
 * @param body 动作附加参数
 */
async function postGeoTargetAction(
  targetId: string,
  action: "approve" | "skip" | "retry",
  body?: Record<string, string>,
) {
  const response = await httpClient.post<ApiEnvelope<GeoTargetItemRaw>>(
    `/geo-governance/targets/${targetId}/${action}`,
    body,
  );
  if (response.data.code !== "OK") {
    throw new Error("GEO 治理对象操作失败");
  }
}
